/**
 * Simple Slideshow using Swiper.js
 * Initializes Swiper on slideshow-component elements
 * Handles dynamic content updates from predictive search
 */

import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

class CustomSlideshow extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.observer = null;
  }

  connectedCallback() {
    console.log('CustomSlideshow connectedCallback');
    this.initSwiper();
    this.observeChanges();
  }

  initSwiper() {
    // Destroy existing instance if any
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }

    const container = this.querySelector('[ref="slideshowContainer"]');
    const scroller = this.querySelector('[ref="scroller"]');
    const slides = this.querySelectorAll('[ref="slides[]"]');
    const prevBtn = this.querySelector('[ref="previous"]');
    const nextBtn = this.querySelector('[ref="next"]');

    if (!container || !scroller || !slides.length) {
      console.warn('Slideshow: required elements not found');
      return;
    }

    // Add Swiper classes
    container.classList.add('swiper');
    scroller.classList.add('swiper-wrapper');
    slides.forEach(slide => slide.classList.add('swiper-slide'));

    // Get configuration
    const loop = this.hasAttribute('infinite');
    const autoplaySpeed = parseInt(this.getAttribute('autoplay')) || 0;

    // Initialize Swiper
    this.swiper = new Swiper(container, {
      modules: [Navigation, Pagination],
      slidesPerView: 'auto',
      spaceBetween: 16,
      loop: loop,
      navigation: {
        nextEl: nextBtn,
        prevEl: prevBtn,
      },
      autoplay: autoplaySpeed > 0 ? {
        delay: autoplaySpeed,
        disableOnInteraction: false,
      } : false,
      breakpoints: {
        640: {
          slidesPerView: 'auto',
          spaceBetween: 16,
        },
        768: {
          slidesPerView: 'auto',
          spaceBetween: 20,
        },
        1024: {
          slidesPerView: 'auto',
          spaceBetween: 24,
        }
      }
    });

    console.log(`Slideshow ready with ${slides.length} slides using Swiper`);
  }

  observeChanges() {
    // Disconnect existing observer
    if (this.observer) {
      this.observer.disconnect();
    }

    // Watch for child changes (when predictive search updates content)
    this.observer = new MutationObserver((mutations) => {
      // Check if the slideshow-container itself was replaced or slides changed
      const needsReinit = mutations.some(mutation => {
        // Check if container was replaced
        if (mutation.target === this && mutation.addedNodes.length > 0) {
          return Array.from(mutation.addedNodes).some(node =>
            node instanceof HTMLElement && node.hasAttribute('ref') && node.getAttribute('ref') === 'slideshowContainer'
          );
        }
        
        // Check if slides changed
        return Array.from(mutation.addedNodes).some(node => 
          node instanceof HTMLElement && (
            node.hasAttribute('ref') && node.getAttribute('ref').includes('slides') ||
            node.querySelector('[ref="slides[]"]')
          )
        ) || Array.from(mutation.removedNodes).some(node =>
          node instanceof HTMLElement && node.hasAttribute('ref') && node.getAttribute('ref').includes('slides')
        );
      });

      if (needsReinit) {
        console.log('DOM changed, reinitializing Swiper...');
        // Use setTimeout to ensure morph is completely finished
        setTimeout(() => {
          this.initSwiper();
          this.observeChanges();
          // Re-observe after reinitialization
        }, 50);
      }
    });

    // Observe this element (slideshow-component) and its container
    this.observer.observe(this, {
      childList: true,
      subtree: true
    });
  }

  disconnectedCallback() {
    
    // Cleanup observer
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }

    // Cleanup Swiper
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }
}

export default CustomSlideshow;